import { useEffect, useState } from 'react';

/**
 * Progressive mounting for long lists of heavy (Plasmic) items.
 *
 * Problem (docs/performance.md §۳.۳): going back to /s/ on a mid-range phone renders 20
 * product cards (80 closed dialogs) in one synchronous commit — a 2–4s main-thread lock,
 * while only ~2 cards fit on the screen.
 *
 * Rule: during a client-side render pass only the first `IMMEDIATE_PER_PASS` items mount
 * for real; the rest render a cheap placeholder and are upgraded one per frame, *after*
 * paint, so the page shows up at once and stays responsive.
 *
 * SSR and hydration are untouched: until `markHydrated()` runs everything mounts fully,
 * so crawlers get complete HTML and hydration never sees a mismatch.
 */

export const IMMEDIATE_PER_PASS = 3;

let hydrated = false;
let claimedThisPass = 0;
let passResetScheduled = false;
type Upgrade = { cancelled: boolean; run: () => void };
const upgradeQueue: Upgrade[] = [];
let pumping = false;

/** Call once from a top-level effect (runs after the hydration commit). */
export function markHydrated() {
  hydrated = true;
}

/** Test-only. */
export function __resetProgressiveMount() {
  hydrated = false;
  claimedThisPass = 0;
  passResetScheduled = false;
  upgradeQueue.length = 0;
  pumping = false;
}

function claimImmediateSlot(): boolean {
  if (!hydrated) return true;
  // Items rendered in the same synchronous pass share one budget; the timeout fires only
  // after that render has committed.
  if (!passResetScheduled) {
    passResetScheduled = true;
    setTimeout(() => {
      claimedThisPass = 0;
      passResetScheduled = false;
    }, 0);
  }
  return claimedThisPass++ < IMMEDIATE_PER_PASS;
}

const afterNextPaint = (fn: () => void) => {
  if (typeof requestAnimationFrame !== 'function' || document.visibilityState === 'hidden') {
    setTimeout(fn, 0); // rAF does not fire in hidden tabs
    return;
  }
  requestAnimationFrame(() => setTimeout(fn, 0));
};

function pump() {
  if (pumping) return;
  pumping = true;
  const step = () => {
    let next = upgradeQueue.shift();
    while (next?.cancelled) next = upgradeQueue.shift(); // unmounted meanwhile: skip, don't burn a frame
    if (!next) {
      pumping = false;
      return;
    }
    next.run(); // one item per task: its render is the only long work before the next paint
    afterNextPaint(step);
  };
  afterNextPaint(step);
}

/** `true` when the item should render for real, `false` while it should show a placeholder. */
export function useProgressiveMount(): boolean {
  const [ready, setReady] = useState(claimImmediateSlot);

  useEffect(() => {
    if (ready) return;
    const upgrade: Upgrade = { cancelled: false, run: () => setReady(true) };
    upgradeQueue.push(upgrade);
    pump();
    return () => {
      upgrade.cancelled = true;
    };
  }, [ready]);

  return ready;
}
